import { useCallback, useEffect, useRef, useState } from "react";

import { hasAttachmentsDragData } from "@/libs/attachment-drag";

type FileDropProps = {
  onUploadFiles?: (files: File[]) => void;
};

/**
 * Hook to handle file drag-and-drop functionality.
 * @param onUploadFiles - Callback function to handle the uploaded files.
 * @returns An object containing the dropRef to be attached to the drop area,
 *          and isDragging state indicating if files are being dragged over the area.
 *
 * Usage:
 * const { dropRef, isDragging } = useFileDrop({
 *   onUploadFiles: (files) => {
 *     // Handle the uploaded files
 *   }
 * });
 *
 * return (
 *   <div ref={dropRef}>
 *     {isDragging ? "Drop files here" : "Drag files here"}
 *   </div>
 * );
 */
export function useFileDrop({ onUploadFiles: uploadFiles }: FileDropProps) {
  const dropRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const handleUploadFiles = useCallback(
    (files: File[] | FileList) => {
      const list = Array.from(files as any);
      uploadFiles?.(list as File[]);
    },
    [uploadFiles],
  );

  useEffect(() => {
    const element = dropRef.current;
    if (!element) return;

    // Dragging an in-app platform attachment (Files tab / gallery) also
    // makes the browser advertise a "Files" entry for the dragged <img>,
    // so without this gate the same drop would BOTH import the platform
    // attachment (attachment drop zone) AND re-upload its rendered bytes
    // as a new blob file here. Internal drags are the drop zone's job.
    const isOsFileDrag = (e: DragEvent) =>
      !hasAttachmentsDragData(e.dataTransfer) &&
      (e.dataTransfer?.types?.includes("Files") ?? false);

    // In-app attachment drags get the same isDragging highlight, but their
    // drop stays with the attachment drop zone props on the same element —
    // this hook only uploads OS files (see the internal-drag gate in onDrop).
    const isAcceptedDrag = (e: DragEvent) =>
      isOsFileDrag(e) || hasAttachmentsDragData(e.dataTransfer);

    const onDragOver = (e: DragEvent) => {
      if (isAcceptedDrag(e)) {
        e.preventDefault();

        setIsDragging(true);
      }
    };

    const onDragEnter = (e: DragEvent) => {
      if (isAcceptedDrag(e)) {
        e.preventDefault();

        dragCounterRef.current += 1;
        setIsDragging(true);
      }
    };

    const onDragLeave = (e: DragEvent) => {
      if (isAcceptedDrag(e)) {
        e.preventDefault();

        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
          setIsDragging(false);
        }
      }
    };

    const onDrop = (e: DragEvent) => {
      if (isOsFileDrag(e)) {
        e.preventDefault();
      }

      dragCounterRef.current = 0;
      setIsDragging(false);

      if (hasAttachmentsDragData(e.dataTransfer)) {
        return;
      }
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleUploadFiles(files);
      }
    };

    element.addEventListener("dragenter", onDragEnter);
    element.addEventListener("dragover", onDragOver);
    element.addEventListener("dragleave", onDragLeave);
    element.addEventListener("drop", onDrop);

    return () => {
      element.removeEventListener("dragenter", onDragEnter);
      element.removeEventListener("dragover", onDragOver);
      element.removeEventListener("dragleave", onDragLeave);
      element.removeEventListener("drop", onDrop);
    };
  }, [handleUploadFiles]);

  return { dropRef, isDragging };
}
